import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Upload, MapPin, Camera, CheckCircle, AlertCircle, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SpotImport({ eventId, onImportComplete, open, onOpenChange }) {
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['.csv', '.txt', '.xlsx', '.xls', '.docx', '.doc', '.pdf'];
      const fileExt = '.' + file.name.split('.').pop().toLowerCase();
      
      if (!allowedTypes.includes(fileExt)) {
        toast.error('Unsupported file type. Please upload CSV, TXT, XLSX, DOCX, or PDF files.');
        return;
      }
      
      setSelectedFile(file);
      setPreviewData(null); // Reset preview
    }
  };

  const handlePreview = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('event_id', eventId);

    try {
      const response = await axios.post(`${API}/spots/import-preview`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.count === 0) {
        toast.warning('No spots found in file. Please check the file format.');
        return;
      }

      setPreviewData(response.data);
      
      let successMsg = `Found ${response.data.count} spots to import`;
      if (response.data.hyperlinks_found > 0) {
        successMsg += ` (${response.data.hyperlinks_found} with hyperlinks)`;
      }
      toast.success(successMsg);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse file');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!previewData || !previewData.spots) {
      toast.error('No preview data available');
      return;
    }

    setImporting(true);

    try {
      const response = await axios.post(
        `${API}/spots/import-confirm`,
        {
          spots: previewData.spots,
          event_id: eventId
        },
        config
      );

      toast.success(response.data.message);
      
      // Show errors if any
      if (response.data.errors && response.data.errors.length > 0) {
        console.error('Import errors:', response.data.errors);
        
        // Show first few errors in toast
        const errorSummary = response.data.errors.slice(0, 3).join('\n');
        toast.error(`Import errors:\n${errorSummary}`, { duration: 10000 });
      }
      
      setPreviewData(null);
      setSelectedFile(null);
      onOpenChange(false);
      if (onImportComplete) onImportComplete();
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import spots');
    } finally {
      setImporting(false);
    }
  };

  const handleCancel = () => {
    setPreviewData(null);
    setSelectedFile(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Photography Spots</DialogTitle>
        </DialogHeader>

        {!previewData ? (
          // Upload Section
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">How It Works:</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Upload a file containing spot information (CSV, XLSX, TXT, DOCX, PDF)</li>
                <li>• AI will extract: modality, location/address, required gear, and details</li>
                <li>• Review the preview to see what will be imported</li>
                <li>• Addresses will be automatically geocoded to map coordinates</li>
                <li>• Confirm to create all spots at once</li>
              </ul>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Select File</label>
              <input
                type="file"
                accept=".csv,.txt,.xlsx,.xls,.docx,.doc,.pdf"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-semibold
                  file:bg-emerald-50 file:text-emerald-700
                  hover:file:bg-emerald-100
                  cursor-pointer"
              />
              {selectedFile && (
                <div className="mt-2 text-sm text-gray-600 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {selectedFile.name}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handlePreview} disabled={uploading || !selectedFile}>
                {uploading ? 'Parsing...' : 'Preview Import'}
              </Button>
            </div>
          </div>
        ) : (
          // Preview Section
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-emerald-900">
                <CheckCircle className="h-5 w-5" />
                <span className="font-semibold">
                  Found {previewData.count} spots ready to import
                </span>
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Spot Name</th>
                    <th className="px-3 py-2 text-left font-semibold">Modality</th>
                    <th className="px-3 py-2 text-left font-semibold">Location</th>
                    <th className="px-3 py-2 text-left font-semibold">Gear</th>
                    <th className="px-3 py-2 text-center font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.spots.map((spot, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium">{spot.name}</td>
                      <td className="px-3 py-2">
                        <span className="inline-block bg-emerald-100 text-emerald-800 px-2 py-1 rounded text-xs">
                          {spot.modality || 'N/A'}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-start gap-1">
                          <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0 text-gray-500" />
                          <div className="text-xs">
                            {spot.address && <div className="text-gray-700">{spot.address}</div>}
                            {spot.lat && spot.lng && (
                              <div className="text-gray-500">
                                {spot.lat.toFixed(4)}, {spot.lng.toFixed(4)}
                              </div>
                            )}
                            {!spot.address && !spot.lat && (
                              <span className="text-orange-600 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                No location
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-start gap-1">
                          <Camera className="h-3 w-3 mt-0.5 flex-shrink-0 text-gray-500" />
                          <div className="text-xs text-gray-700">
                            {spot.gear && spot.gear.length > 0 
                              ? spot.gear.join(', ') 
                              : 'No gear specified'}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {spot.lat && spot.lng ? (
                          <CheckCircle className="h-4 w-4 text-green-600 mx-auto" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-orange-600 mx-auto" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {previewData.spots.some(s => !s.lat || !s.lng) && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="text-orange-900">
                    <p className="font-semibold">Some spots have no location data</p>
                    <p className="text-xs mt-1">
                      These spots will be created but won't appear on the map until you add coordinates manually.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPreviewData(null)}>
                Back
              </Button>
              <Button onClick={handleConfirmImport} disabled={importing}>
                {importing ? 'Importing...' : `Confirm & Import ${previewData.count} Spots`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
